import React, { useContext, useState, useCallback, DragEvent } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IFileData, TFileExtension } from 'types';
import { postMediaFile } from 'backend/additionalInformation';
import Text from 'components/Text/Text';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import { openBrowseFile } from 'utils/openBrowseFile';
import { createAppError } from 'utils/helpers';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import classNames from 'classnames';
import style from './DragAndDropForFile.module.scss';

interface IDragAndDropForFile {
	changeFileData: (value: IFileData) => void;
	onImageFileSizeExceeded: () => void;
	className?: string;
	additionalDescription?: string;
	accept?: TFileExtension[];
}

const DragAndDropForFile: React.FC<IDragAndDropForFile> = ({
	changeFileData,
	onImageFileSizeExceeded,
	className,
	additionalDescription,
	accept = ['jpg', 'jpeg', 'png', 'mp4', 'pdf', 'zip', 'bin', 'apk', 'html', 'json', 'p12'],
}) => {
	const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
	const [isDragOver, setIsDragOver] = useState(false);
	const containerClasses = classNames(style.container, className, { [style.over]: isDragOver });
	const { t } = useTranslation('error');
	const maxFileSizeMB = 1;

	const sendForm = useCallback(
		async (files: FileList | null) => {
			if (files) {
				if (!accept.includes(files[0].name.toLowerCase().split('.').pop() as TFileExtension)) {
					addAppError({
						id: uuidv4(),
						message: t('sorryWeDoNotSupportThatTypeOfFile'),
						name: 'noErrorData',
					});
					return;
				}

				const file = files[0];

				if (file.size > 1000 * 1000 * maxFileSizeMB) {
					onImageFileSizeExceeded();
					return null;
				}

				showLoader();
				const formData = new FormData();
				formData.append('file', file, file.name);
				try {
					const { data } = await postMediaFile({ data: formData });
					changeFileData({ fileId: data.fileId, fileHash: data.fileHash, fileName: file.name });
				} catch (err) {
					const error = err as Error;
					const appError = createAppError(error);
					addAppError(appError);
				} finally {
					hideLoader();
				}
			} else {
				addAppError({
					id: uuidv4(),
					message: t('sorryWeDoNotSupportThatTypeOfFile'),
					name: 'noErrorData',
				});
			}
		},
		[accept, addAppError, changeFileData, hideLoader, onImageFileSizeExceeded, showLoader, t],
	);

	const handleUpload = useCallback(() => {
		openBrowseFile({
			onChoose: sendForm,
			accept: accept.map((extension) => '.' + extension).join(', '),
		});
	}, [accept, sendForm]);

	const handleDrag = useCallback(
		(e: DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			e.stopPropagation();
			if (e.type === 'dragover' && !isDragOver) {
				setIsDragOver(true);
			} else if (e.type === 'dragleave' && isDragOver) {
				setIsDragOver(false);
			}
		},
		[isDragOver],
	);

	const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOver(false);
		if (e.dataTransfer.files && e.dataTransfer.files[0]) {
			const files = e.dataTransfer.files;
			if (files.length > 1) {
				addAppError({
					id: uuidv4(),
					message: t('noMoreThanOneFileAllowed'),
					name: 'noErrorData',
				});
			} else if (!accept.includes(files[0].name.toLowerCase().split('.').pop() as TFileExtension)) {
				addAppError({
					id: uuidv4(),
					message: t('sorryWeDoNotSupportThatTypeOfFile'),
					name: 'noErrorData',
				});
			} else if (files[0].size > 1000 * 1000 * maxFileSizeMB) {
				onImageFileSizeExceeded();
			} else {
				sendForm(files);
			}
		}
	}, []);

	return (
		<div
			className={containerClasses}
			onClick={handleUpload}
			onDragEnter={handleDrag}
			onDragLeave={handleDrag}
			onDragOver={handleDrag}
			onDrop={handleDrop}>
			<svg>
				<use xlinkHref={`${breadcrumbs}#uploadArrow`} />
			</svg>
			<div>
				<Text
					translationNameSpace="form"
					theme="secondary"
					size="s"
					weight="bold"
					display="inline"
					translationKey="clickToUpload"
				/>{' '}
				<Text
					translationNameSpace="form"
					theme="neutralLight"
					size="s"
					weight="bold"
					display="inline"
					translationKey="orDragAndDrop"
				/>
			</div>
			{additionalDescription && (
				<Text
					translationNameSpace="form"
					theme="neutralLight"
					size="s"
					weight="bold"
					align="center"
					translationKey={additionalDescription}
				/>
			)}
		</div>
	);
};

export default React.memo(DragAndDropForFile);
